import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { SurveyConfig, SurveyQuestion, SurveySubmission, SurveyResponse as SurveyResponseType } from '../types/PluginTypes';
import { SurveyStorage, exportToCSV, generateAnalytics } from '../utils/SurveyUtils';
import { DocumentService } from '../services/DocumentService';
import AutoTranslateText from '../components/AutoTranslateText';

interface SurveyPluginProps {
  config: SurveyConfig;
}

// ✅ RENOMMAGE pour éviter le conflit avec le type SurveyResponse importé
interface SurveyState {
  [questionId: string]: {
    value: any;
    timeSpent?: number;
  };
}

// ✅ 1. MOTEUR DE LOGIQUE CONDITIONNELLE (Local)
// Fonction pure pour vérifier si une question doit être affichée
const checkVisibility = (question: SurveyQuestion, responses: SurveyState): boolean => {
  // Pas de logique conditionnelle ? Toujours visible
  if (!question.conditionalLogic || !question.conditionalLogic.dependsOn) return true;
  
  const { dependsOn, showIf, value: targetValue } = question.conditionalLogic;
  const parentResponse = responses[dependsOn];
  
  // Si la question parente n'a pas encore de réponse, on masque l'enfant
  if (!parentResponse || parentResponse.value === undefined || parentResponse.value === null || parentResponse.value === '') {
      return false;
  }

  const actualValue = parentResponse.value;

  // Comparaison selon l'opérateur
  switch (showIf) {
    case 'equals': return String(actualValue) === String(targetValue);
    case 'not_equals': return String(actualValue) !== String(targetValue);
    case 'greater_than': return Number(actualValue) > Number(targetValue);
    case 'less_than': return Number(actualValue) < Number(targetValue);
    case 'contains': return String(actualValue).toLowerCase().includes(String(targetValue).toLowerCase());
    case 'selected': return Array.isArray(actualValue) && actualValue.includes(targetValue);
    case 'not_selected': return Array.isArray(actualValue) && !actualValue.includes(targetValue);
    default: return true;
  }
};

// ✅ 2. MOTEUR DE VALIDATION (Local)
// Vérifie les contraintes (required, email, phone, min/max...)
const validateAnswer = (question: SurveyQuestion, value: any): { isValid: boolean, message?: string } => {
  // 1. Vérification "Champ requis"
  if (question.required) {
      if (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
          return { isValid: false, message: 'Ce champ est obligatoire.' };
      }
  }
  
  // Si vide et non requis, c'est valide (on saute les autres checks)
  if (!value && value !== 0 && value !== false) return { isValid: true };

  const valStr = String(value);

  // 2. Validation par Type
  if (question.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(valStr)) {
      return { isValid: false, message: 'Format email invalide.' };
  }
  if (question.type === 'phone' && !/^[\d\s\+\-\(\)]{8,}$/.test(valStr)) {
      return { isValid: false, message: 'Format téléphone invalide.' };
  }
  if (question.type === 'url' && !/^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/.test(valStr)) {
      return { isValid: false, message: 'Format URL invalide.' };
  }

  // 3. Validation Numérique (Min/Max)
  if (question.type === 'number' && question.validation) {
      const num = Number(value);
      if (question.validation.min !== undefined && num < question.validation.min) {
           return { isValid: false, message: question.validation.customMessage || `La valeur doit être au moins ${question.validation.min}.` };
      }
      if (question.validation.max !== undefined && num > question.validation.max) {
           return { isValid: false, message: question.validation.customMessage || `La valeur ne doit pas dépasser ${question.validation.max}.` };
      }
  }

  // 4. Validation Texte (Longueur)
  if ((question.type === 'text' || question.type === 'textarea') && question.validation) {
      if (question.validation.min && valStr.length < question.validation.min) {
          return { isValid: false, message: question.validation.customMessage || `Au moins ${question.validation.min} caractères requis.` };
      }
      if (question.validation.max && valStr.length > question.validation.max) {
           return { isValid: false, message: question.validation.customMessage || `Maximum ${question.validation.max} caractères.` };
      }
  }

  return { isValid: true };
};

// Styles CSS injectés pour le thème
const SurveyThemeStyles: React.FC<{ primaryColor?: string }> = ({ primaryColor = '#0d6efd' }) => (
  <style>{`
    .survey-plugin.theme-minimal { border: none; box-shadow: none; background-color: #fff; }
    .theme-minimal .card-header { background: none; border-bottom: 1px solid #eee; }
    .theme-modern { border-radius: 12px; box-shadow: 0 8px 25px rgba(0,0,0,0.1); }
    .theme-modern .card-header { background-color: #f8f9fa; border-top-left-radius: 12px; border-top-right-radius: 12px; }
    .theme-modern .btn { border-radius: 50px; font-weight: 600; }
    .survey-plugin .rating-group .btn:hover { transform: scale(1.1); }
    
    /* Responsive Helpers */
    .survey-plugin .form-label { word-break: break-word; }
  `}</style>
);

const SurveyPlugin: React.FC<SurveyPluginProps> = ({ config }) => {
  // ✅ Mise à jour du type de l'état
  const [responses, setResponses] = useState<SurveyState>({});
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [questionStartTime, setQuestionStartTime] = useState(Date.now());

  // --- SÉCURITÉ : Anti-Spam & Captcha ---
  const formStartRef = useRef<number>(Date.now());
  const [honeypot, setHoneypot] = useState('');
  const [securityError, setSecurityError] = useState<string | null>(null);

  // Lecture de la config avec fallback
  const mode = (config as any).mode || 'survey';
  const scoringEnabled = mode !== 'survey';
  const pointsGoal = (config as any).pointsGoal ?? 0;
  const examScoreScale = (config as any).examScoreScale ?? 20;
  const antiSpamEnabled = (config as any).enableAntiSpam ?? (config as any).enableSpamProtection ?? false;
  const requireCaptcha = (config as any).requireCaptcha ?? false;
  const antiSpamMinSeconds = (config as any).antiSpamMinSeconds ?? 5;

  const [captchaA, setCaptchaA] = useState<number>(0);
  const [captchaB, setCaptchaB] = useState<number>(0);
  const [captchaInput, setCaptchaInput] = useState<string>('');

  // Initialisation Captcha
  useEffect(() => {
    if (requireCaptcha) {
      setCaptchaA(Math.floor(1 + Math.random() * 9));
      setCaptchaB(Math.floor(1 + Math.random() * 9));
      setCaptchaInput('');
    }
  }, [requireCaptcha]);

  // Expiration date
  if (config.expirationDate) {
    if (new Date() > new Date(config.expirationDate)) {
      return (
        <div className="card text-center p-5">
          <h3 className="text-warning">⏳ Questionnaire expiré</h3>
          <p>Ce questionnaire n'accepte plus de réponses.</p>
        </div>
      );
    }
  }

  // ✅ Calcul des questions visibles selon la Logique Conditionnelle
  const visibleQuestions = useMemo(() => {
    // On filtre les questions en utilisant notre moteur local checkVisibility
    return (config.questions || []).filter(question => checkVisibility(question, responses));
  }, [config.questions, responses]);

  const totalVisibleQuestions = visibleQuestions.length;
  const currentQuestion = visibleQuestions[currentStep];

  const totalPossibleScore = useMemo(() => {
    if (!scoringEnabled) return 0;
    return visibleQuestions.reduce((sum, question) => {
      const correctAnswers = (question.correctAnswers || []).map(String).filter(Boolean);
      if (!['radio', 'checkbox', 'select'].includes(question.type) || correctAnswers.length === 0) return sum;
      if (question.type === 'checkbox' && question.scoringMode === 'partial') {
        const perCorrect = typeof question.partialPointsPerCorrect === 'number' ? question.partialPointsPerCorrect : 1;
        return sum + (correctAnswers.length * perCorrect);
      }
      return sum + (typeof question.points === 'number' ? question.points : 1);
    }, 0);
  }, [visibleQuestions, scoringEnabled]);

  const currentScore = useMemo(() => {
    if (!scoringEnabled) return 0;
    return visibleQuestions.reduce((sum, question) => {
      const correctAnswers = (question.correctAnswers || []).map(String).filter(Boolean);
      if (!['radio', 'checkbox', 'select'].includes(question.type) || correctAnswers.length === 0) return sum;
      const response = responses[question.id]?.value;
      if (response === undefined || response === null || response === '' || (Array.isArray(response) && response.length === 0)) {
        return sum;
      }

      const points = typeof question.points === 'number' ? question.points : 1;

      if (question.type === 'checkbox') {
        const responseValues = Array.isArray(response) ? response.map(String) : [];
        if (question.scoringMode === 'partial') {
          const perCorrect = typeof question.partialPointsPerCorrect === 'number' ? question.partialPointsPerCorrect : 1;
          const perWrong = typeof question.partialPointsPerWrong === 'number' ? question.partialPointsPerWrong : 1;
          const correctSelected = responseValues.filter(val => correctAnswers.includes(val)).length;
          const wrongSelected = responseValues.filter(val => !correctAnswers.includes(val)).length;
          const rawScore = (correctSelected * perCorrect) - (wrongSelected * perWrong);
          return sum + rawScore;
        }

        const isCorrect =
          responseValues.length === correctAnswers.length &&
          responseValues.every(val => correctAnswers.includes(val)) &&
          correctAnswers.every(val => responseValues.includes(val));
        return isCorrect ? sum + points : sum;
      }

      const isCorrect = correctAnswers.includes(String(response));
      return isCorrect ? sum + points : sum;
    }, 0);
  }, [visibleQuestions, responses, scoringEnabled]);

  const examGrade = totalPossibleScore > 0
    ? Math.round((currentScore / totalPossibleScore) * examScoreScale * 10) / 10
    : 0;

  // Calcul progression
  const progressPercentage = useMemo(() => {
    if (totalVisibleQuestions === 0) return 0;
    // On compte combien de questions VISIBLES ont une réponse
    const answeredCount = visibleQuestions.filter(q => {
      const val = responses[q.id]?.value;
      return val !== undefined && val !== null && val !== '';
    }).length;
    return (answeredCount / totalVisibleQuestions) * 100;
  }, [visibleQuestions, responses]);

  // Mise à jour réponse
  const handleResponseChange = useCallback((questionId: string, value: any) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        value: value
      }
    }));
    // Nettoyage de l'erreur à la saisie
    if (errors[questionId]) {
      setErrors(prev => {
        const newErr = { ...prev };
        delete newErr[questionId];
        return newErr;
      });
    }
  }, [errors]);

  // Tracking temps (Analytics)
  useEffect(() => {
    if (!config.enableAnalytics || currentStep === 0) {
        setQuestionStartTime(Date.now());
        return;
    }
    // Sauvegarder le temps de la question précédente au changement d'étape (si on avance)
    // (Simplification : on reset juste le timer ici pour la nouvelle question)
    setQuestionStartTime(Date.now());
  }, [currentStep, config.enableAnalytics]);

  // Navigation Suivant
  const handleNext = () => {
    if (!currentQuestion) return;

    // Validation Locale
    const validation = validateAnswer(currentQuestion, responses[currentQuestion.id]?.value);
    
    if (!validation.isValid) {
      setErrors({ [currentQuestion.id]: validation.message || 'Réponse invalide' });
      return;
    }

    // Sauvegarde temps écoulé pour cette question
    if (config.enableAnalytics) {
        const timeSpent = Date.now() - questionStartTime;
        setResponses(prev => ({
            ...prev,
            [currentQuestion.id]: { ...prev[currentQuestion.id], timeSpent: (prev[currentQuestion.id]?.timeSpent || 0) + timeSpent }
        }));
    }

    if (currentStep < totalVisibleQuestions - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) setCurrentStep(prev => prev - 1);
  };

  // Soumission
  const handleSubmit = async () => {
    // --- VÉRIFICATION ANTI-SPAM ---
    setSecurityError(null);

    // 1. Honeypot (Pot de miel)
    if (antiSpamEnabled && honeypot.trim() !== '') {
        console.warn('Spam detected (honeypot)');
        // On fait semblant que ça a marché pour ne pas alerter le bot
        setIsSubmitted(true); 
        return; 
    }

    // 2. Timing (Humain vs Machine)
    if (antiSpamEnabled) {
        const elapsedSec = (Date.now() - formStartRef.current) / 1000;
        if (elapsedSec < antiSpamMinSeconds) {
            setSecurityError(`Veuillez prendre le temps de lire les questions (${Math.ceil(antiSpamMinSeconds - elapsedSec)}s restantes).`);
            return;
        }
    }

    // 3. Captcha Math
    if (requireCaptcha) {
        const provided = parseInt(captchaInput, 10);
        if (isNaN(provided) || provided !== (captchaA + captchaB)) {
            setSecurityError('Calcul incorrect. Êtes-vous un robot ? 🤖');
            return;
        }
    }

    // Validation finale de toutes les questions visibles
    const newErrors: {[key: string]: string} = {};
    let hasError = false;
    visibleQuestions.forEach(q => {
        const val = validateAnswer(q, responses[q.id]?.value);
        if (!val.isValid) {
            newErrors[q.id] = val.message || 'Invalide';
            hasError = true;
        }
    });

    if (hasError) {
        setErrors(newErrors);
        // Revenir à la première question en erreur
        const firstErrIdx = visibleQuestions.findIndex(q => newErrors[q.id]);
        if (firstErrIdx !== -1) setCurrentStep(firstErrIdx);
        return;
    }

    // Construction Payload
    const submittedAt = new Date();

    // Calcul du taux de complétion
    const answeredCount = visibleQuestions.filter(q => {
      const val = responses[q.id]?.value;
      return val !== undefined && val !== null && val !== '' && (Array.isArray(val) ? val.length > 0 : true);
    }).length;
    
    // Si toutes les questions visibles sont obligatoires et validées, ce sera 100%
    const completionRate = totalVisibleQuestions > 0 ? Math.round((answeredCount / totalVisibleQuestions) * 100) : 0;

    // ✅ CORRECTION DU TYPE : Ajout de timestamp et cast explicite
    const submissionResponses: SurveyResponseType[] = visibleQuestions.map(q => ({
        questionId: q.id,
        value: responses[q.id]?.value,
        timestamp: submittedAt, // Ajout de la propriété obligatoire
        timeSpent: responses[q.id]?.timeSpent || 0
    }));

    const submission: SurveySubmission = {
        id: `sub_${Date.now()}`,
        submittedAt,
        completionRate, // Propriété obligatoire ajoutée
        totalTimeSpent: Math.round(Object.values(responses).reduce((acc: number, r: any) => acc + (r?.timeSpent || 0), 0) / 1000),
        responses: submissionResponses
    };

    console.log('Soumission Survey:', submission);
    
    // Sauvegarde locale (Analytics) et API
    const surveyId = (config.title || 'survey').toLowerCase().replace(/\s+/g, '_');
    
    try {
        const ownerIdRaw = (config as any).userId || (config as any).ownerId;
        const ownerId = ownerIdRaw !== undefined && ownerIdRaw !== null ? Number(ownerIdRaw) : undefined;
        const ownerEmail = (config as any).ownerEmail || (config as any).notificationEmail;

        // ✅ AJOUT : await pour s'assurer que la donnée est partie
        await SurveyStorage.saveSubmission(surveyId, submission, {
            ownerId: Number.isFinite(ownerId) ? ownerId : undefined,
            ownerEmail: ownerEmail || undefined
        });
    } catch (err) {
        console.warn('Erreur sauvegarde API, données enregistrées localement.');
        // On ne bloque pas l'utilisateur, on continue vers l'email/merci
    }

    // Notification + Email au propriétaire
    const ownerEmail = (config as any).ownerEmail || (config as any).notificationEmail;

    if (ownerEmail) {
        try {
            const blob = new Blob([JSON.stringify(submission, null, 2)], { type: 'application/json' });
            const filename = `reponse-questionnaire-${submittedAt.toISOString().slice(0,10)}.json`;
            await DocumentService.sendSignedDocument(blob, ownerEmail, `Nouvelle réponse : ${config.title}`, filename);
        } catch (e) {
            console.error('Erreur envoi email:', e);
        }
    }

    setIsSubmitted(true);
    
    if ((config as any).redirectUrl) {
        setTimeout(() => window.location.href = (config as any).redirectUrl, 2000);
    }
  };

  const primaryColor = (config.primaryColor && config.primaryColor.startsWith('#')) ? config.primaryColor : '#0d6efd';

  if (isSubmitted) {
    const goalReached = scoringEnabled && mode === 'game' && pointsGoal > 0 ? currentScore >= pointsGoal : false;
    return (
      <div className="card text-center p-5 border-0 shadow-sm" style={{ borderRadius: '12px' }}>
        <div className="display-1 mb-3">🎉</div>
        <h3 className="mb-3">Merci !</h3>
        <p className="text-muted">{config.thankYouMessage}</p>
        {scoringEnabled && totalPossibleScore > 0 && (
          <div className="mt-3">
            <div className="fw-semibold">Score : {currentScore} / {totalPossibleScore} pts</div>
            {mode === 'game' && pointsGoal > 0 && (
              <div className={`small ${goalReached ? 'text-success' : 'text-muted'}`}>
                {goalReached ? 'Objectif atteint ✅' : `Objectif : ${pointsGoal} pts`}
              </div>
            )}
            {mode === 'exam' && (
              <div className="small text-muted">Note : {examGrade} / {examScoreScale}</div>
            )}
          </div>
        )}
      </div>
    );
  }

  if (totalVisibleQuestions === 0) {
    return <div className="p-4 text-center text-muted">Aucune question disponible.</div>;
  }

  return (
    <div className={`survey-plugin card`} style={{ '--bs-primary': primaryColor } as React.CSSProperties}>
      <SurveyThemeStyles primaryColor={primaryColor} />
      
      <div className="card-header border-bottom-0 pb-0">
        <h5 className="mb-1">{config.title}</h5>
        {config.description && (
          <div
            className="text-muted small mb-2"
            dangerouslySetInnerHTML={{ __html: config.description }}
          />
        )}
        {scoringEnabled && totalPossibleScore > 0 && (
          <div className="d-flex flex-wrap gap-2 text-muted small">
            <span>Score : {currentScore} / {totalPossibleScore} pts</span>
            {mode === 'game' && pointsGoal > 0 && <span>Objectif : {pointsGoal} pts</span>}
            {mode === 'exam' && <span>Note (est.) : {examGrade} / {examScoreScale}</span>}
          </div>
        )}
        
        {config.showProgressBar && (
          <div className="progress mt-3" style={{ height: '6px', borderRadius: '4px' }}>
            <div 
              className="progress-bar" 
              style={{ width: `${progressPercentage}%`, backgroundColor: primaryColor, transition: 'width 0.4s ease' }} 
            />
          </div>
        )}
      </div>

      <div className="card-body">
        {/* Anti-Spam Honeypot (Invisible) */}
        {antiSpamEnabled && (
            <input 
                type="text" 
                value={honeypot} 
                onChange={e => setHoneypot(e.target.value)} 
                style={{ position: 'absolute', opacity: 0, height: 0, width: 0, zIndex: -1 }} 
                autoComplete="off"
            />
        )}

        {/* Info Étape */}
        <div className="d-flex justify-content-between align-items-center mb-4 text-muted small">
            <span>Question {currentStep + 1} / {totalVisibleQuestions}</span>
            <span>{Math.round(progressPercentage)}%</span>
        </div>

        {/* -------- RENDU QUESTION -------- */}
        {currentQuestion && (
            <div className="mb-4">
                <label className="form-label fw-bold mb-2">
                    {currentQuestion.title}
                    {currentQuestion.required && <span className="text-danger ms-1">*</span>}
                </label>
                {currentQuestion.description && <p className="small text-muted mb-3">{currentQuestion.description}</p>}

                <div className="question-input-area">
                    {/* TEXT / TEXTAREA */}
                    {currentQuestion.type === 'text' && (
                        <input type="text" className={`form-control ${errors[currentQuestion.id] ? 'is-invalid' : ''}`} placeholder={currentQuestion.placeholder} value={responses[currentQuestion.id]?.value || ''} onChange={e => handleResponseChange(currentQuestion.id, e.target.value)} />
                    )}
                    {currentQuestion.type === 'textarea' && (
                        <textarea className={`form-control ${errors[currentQuestion.id] ? 'is-invalid' : ''}`} rows={4} placeholder={currentQuestion.placeholder} value={responses[currentQuestion.id]?.value || ''} onChange={e => handleResponseChange(currentQuestion.id, e.target.value)} />
                    )}
                    
                    {/* EMAIL / PHONE / URL / NUMBER */}
                    {['email', 'phone', 'url', 'number'].includes(currentQuestion.type) && (
                         <input type={currentQuestion.type === 'phone' ? 'tel' : currentQuestion.type} className={`form-control ${errors[currentQuestion.id] ? 'is-invalid' : ''}`} placeholder={currentQuestion.placeholder} value={responses[currentQuestion.id]?.value || ''} onChange={e => handleResponseChange(currentQuestion.id, e.target.value)} />
                    )}

                    {/* DATE */}
                    {currentQuestion.type === 'date' && (
                         <input type="date" className={`form-control ${errors[currentQuestion.id] ? 'is-invalid' : ''}`} value={responses[currentQuestion.id]?.value || ''} onChange={e => handleResponseChange(currentQuestion.id, e.target.value)} />
                    )}

                    {/* SELECT */}
                    {currentQuestion.type === 'select' && (
                        <select className={`form-select ${errors[currentQuestion.id] ? 'is-invalid' : ''}`} value={responses[currentQuestion.id]?.value || ''} onChange={e => handleResponseChange(currentQuestion.id, e.target.value)}>
                            <option value="">Sélectionner...</option>
                            {(currentQuestion.options || []).map((opt, i) => <option key={i} value={opt}>{opt}</option>)}
                        </select>
                    )}

                    {/* RADIO */}
                    {currentQuestion.type === 'radio' && (
                        <div className="d-flex flex-column gap-2">
                            {(currentQuestion.options || []).map((opt, i) => (
                                <div key={i} className="form-check">
                                    <input className="form-check-input" type="radio" name={currentQuestion.id} id={`${currentQuestion.id}_${i}`} checked={responses[currentQuestion.id]?.value === opt} onChange={() => handleResponseChange(currentQuestion.id, opt)} />
                                    <label className="form-check-label" htmlFor={`${currentQuestion.id}_${i}`}>{opt}</label>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* CHECKBOX */}
                    {currentQuestion.type === 'checkbox' && (
                         <div className="d-flex flex-column gap-2">
                            {(currentQuestion.options || []).map((opt, i) => {
                                const currentVals = (responses[currentQuestion.id]?.value as string[]) || [];
                                return (
                                <div key={i} className="form-check">
                                    <input className="form-check-input" type="checkbox" id={`${currentQuestion.id}_${i}`} checked={currentVals.includes(opt)} onChange={e => {
                                        const newVals = e.target.checked ? [...currentVals, opt] : currentVals.filter(v => v !== opt);
                                        handleResponseChange(currentQuestion.id, newVals);
                                    }} />
                                    <label className="form-check-label" htmlFor={`${currentQuestion.id}_${i}`}>{opt}</label>
                                </div>
                                )
                            })}
                         </div>
                    )}

                    {/* RATING */}
                    {currentQuestion.type === 'rating' && (
                        <div className="d-flex gap-2 rating-group">
                            {Array.from({ length: currentQuestion.ratingMax || 5 }, (_, i) => i + 1).map(num => (
                                <button key={num} type="button" className={`btn ${responses[currentQuestion.id]?.value === num ? 'btn-warning text-white' : 'btn-outline-secondary'}`} style={{ width: 40, height: 40 }} onClick={() => handleResponseChange(currentQuestion.id, num)}>
                                    {num}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                
                {/* Message Erreur Question */}
                {errors[currentQuestion.id] && (
                    <div className="text-danger small mt-2">
                        <i className="fas fa-exclamation-circle me-1"></i> {errors[currentQuestion.id]}
                    </div>
                )}
            </div>
        )}

        {/* --- CAPTCHA --- */}
        {requireCaptcha && (currentStep === totalVisibleQuestions - 1) && (
             <div className="alert alert-light border mt-4">
                 <label className="form-label small fw-bold">Vérification de sécurité</label>
                 <div className="d-flex align-items-center gap-2">
                     <span className="badge bg-secondary p-2 noselect">{captchaA} + {captchaB} = ?</span>
                     <input type="number" className="form-control" style={{ width: 80 }} value={captchaInput} onChange={e => setCaptchaInput(e.target.value)} placeholder="?" />
                 </div>
                 {securityError && <div className="text-danger small mt-2">{securityError}</div>}
             </div>
        )}

        {/* --- BOUTONS NAVIGATION --- */}
        <div className="d-flex justify-content-between mt-4 pt-3 border-top">
             <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handlePrevious} disabled={currentStep === 0}>
                 <AutoTranslateText sourceLang="fr">Précédent</AutoTranslateText>
             </button>

             {currentStep < totalVisibleQuestions - 1 ? (
                 <button type="button" className="btn btn-primary btn-sm" style={{ backgroundColor: primaryColor }} onClick={handleNext}>
                     <AutoTranslateText sourceLang="fr">Suivant</AutoTranslateText>
                 </button>
             ) : (
                 <button type="button" className="btn btn-success btn-sm" style={{ backgroundColor: primaryColor }} onClick={handleSubmit}>
                     {config.submitButtonText || <AutoTranslateText sourceLang="fr">Envoyer</AutoTranslateText>}
                 </button>
             )}
        </div>

      </div>
    </div>
  );
};

export default SurveyPlugin;
